import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Trash2, Upload, GripVertical, Sparkles, Wand2, Languages, Palette } from 'lucide-react';
import { createComic, createEpisode, uploadImage, getGenres } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const CreatePage = () => {
  const navigate = useNavigate();
  const { isCreator } = useAuth();
  
  const [genres, setGenres] = useState([]);
  const [activeTab, setActiveTab] = useState('comic');
  
  // Comic creation state
  const [comicData, setComicData] = useState({
    title: '',
    description: '',
    cover_image: '',
    genre: '',
    is_free: true
  });
  const [coverPreview, setCoverPreview] = useState('');
  
  // Episode creation state
  const [episodeData, setEpisodeData] = useState({
    comicId: '',
    title: '',
    episode_number: 1,
    panels: [],
    is_free: true
  });
  
  // PDF creation state
  const [pdfPages, setPdfPages] = useState([]);
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const res = await getGenres();
        setGenres(res.data);
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };
    fetchGenres();
  }, []);

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const res = await uploadImage(file);
      setComicData({ ...comicData, cover_image: res.data.url });
      setCoverPreview(res.data.url);
      toast.success('Cover uploaded');
    } catch (error) {
      toast.error('Failed to upload cover');
    }
  };

  const handlePanelUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    try {
      const uploadPromises = files.map(file => uploadImage(file));
      const results = await Promise.all(uploadPromises);
      const urls = results.map(res => res.data.url);
      
      setEpisodeData(prev => ({
        ...prev,
        panels: [...prev.panels, ...urls]
      }));
      toast.success(`${files.length} panel(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload panels');
    }
  };

  const handlePdfPageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    try {
      const uploadPromises = files.map(file => uploadImage(file));
      const results = await Promise.all(uploadPromises);
      const urls = results.map(res => res.data.url);
      
      setPdfPages(prev => [...prev, ...urls]);
      toast.success(`${files.length} page(s) added`);
    } catch (error) {
      toast.error('Failed to upload pages');
    }
  };

  const removePanel = (index) => {
    setEpisodeData(prev => ({
      ...prev,
      panels: prev.panels.filter((_, i) => i !== index)
    }));
  };

  const removePdfPage = (index) => {
    setPdfPages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateComic = async (e) => {
    e.preventDefault();
    
    if (!comicData.title || !comicData.description || !comicData.genre) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await createComic(comicData);
      toast.success('Comic created successfully!');
      navigate(`/comic/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create comic');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEpisode = async (e) => {
    e.preventDefault();
    
    if (!episodeData.comicId || !episodeData.title || episodeData.panels.length === 0) {
      toast.error('Please fill in all fields and upload panels');
      return;
    }
    
    setLoading(true);
    try {
      await createEpisode(episodeData.comicId, {
        title: episodeData.title,
        episode_number: episodeData.episode_number,
        panels: episodeData.panels,
        is_free: episodeData.is_free
      });
      toast.success('Episode created successfully!');
      navigate(`/comic/${episodeData.comicId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create episode');
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = async () => {
    if (pdfPages.length === 0) {
      toast.error('Please add some pages first');
      return;
    }
    
    setLoading(true);
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      for (let i = 0; i < pdfPages.length; i++) {
        if (i > 0) pdf.addPage();
        
        // Load image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = pdfPages[i];
        
        await new Promise((resolve) => {
          img.onload = resolve;
        });
        
        pdf.addImage(img, 'JPEG', 0, 0, pageWidth, pageHeight);
      }
      
      pdf.save('comic-pages.pdf');
      toast.success('PDF generated successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  if (!isCreator) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center page-padding">
        <h1 className="text-2xl font-bold mb-4">Artist Access Required</h1>
        <p className="text-text-secondary mb-6">You need to be an artist to create content.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 pt-8 page-padding" data-testid="create-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold mb-6">Create</h1>

        {/* AI Studio Banner */}
        <Link 
          to="/ai-tools"
          className="block mb-8 p-5 rounded-2xl bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                <Sparkles size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-teko text-xl text-purple-400 group-hover:text-purple-300">AI Studio</h3>
                <p className="text-sm text-[var(--text-secondary)]">Colorize, translate, generate covers & enhance your art</p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-purple-400">
              <Palette size={16} />
              <Languages size={16} />
              <Wand2 size={16} />
            </div>
          </div>
        </Link>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-surface border border-border mb-8">
            <TabsTrigger value="comic" data-testid="tab-comic">New Comic</TabsTrigger>
            <TabsTrigger value="episode" data-testid="tab-episode">New Episode</TabsTrigger>
            <TabsTrigger value="pdf" data-testid="tab-pdf">Export PDF</TabsTrigger>
          </TabsList>

          {/* Create Comic */}
          <TabsContent value="comic">
            <form onSubmit={handleCreateComic} className="space-y-6">
              <div className="space-y-2">
                <Label>Cover Image</Label>
                <div className="flex gap-4">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Cover" className="w-32 h-44 object-cover border border-border" />
                  ) : (
                    <div className="w-32 h-44 border border-dashed border-border flex items-center justify-center text-text-secondary">
                      No cover
                    </div>
                  )}
                  <label className="flex-1 border border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    <Upload size={32} className="text-text-secondary mb-2" />
                    <span className="text-text-secondary">Upload Cover</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} data-testid="cover-upload" />
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={comicData.title}
                  onChange={(e) => setComicData({ ...comicData, title: e.target.value })}
                  placeholder="Your comic title"
                  className="bg-surface border-border"
                  data-testid="comic-title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={comicData.description}
                  onChange={(e) => setComicData({ ...comicData, description: e.target.value })}
                  placeholder="Tell readers about your comic..."
                  className="bg-surface border-border min-h-[100px]"
                  data-testid="comic-description"
                />
              </div>

              <div className="space-y-2">
                <Label>Genre</Label>
                <Select value={comicData.genre} onValueChange={(v) => setComicData({ ...comicData, genre: v })}>
                  <SelectTrigger className="bg-surface border-border" data-testid="comic-genre">
                    <SelectValue placeholder="Select genre" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-border">
                    {genres.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button type="submit" disabled={loading} className="btn-neo w-full disabled:opacity-50" data-testid="create-comic-btn">
                {loading ? 'Creating...' : 'Create Comic'}
              </button>
            </form>
          </TabsContent>

          {/* Create Episode */}
          <TabsContent value="episode">
            <form onSubmit={handleCreateEpisode} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="comicId">Comic ID</Label>
                <Input
                  id="comicId"
                  value={episodeData.comicId}
                  onChange={(e) => setEpisodeData({ ...episodeData, comicId: e.target.value })}
                  placeholder="Enter comic ID"
                  className="bg-surface border-border"
                  data-testid="episode-comic-id"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="epTitle">Episode Title</Label>
                  <Input
                    id="epTitle"
                    value={episodeData.title}
                    onChange={(e) => setEpisodeData({ ...episodeData, title: e.target.value })}
                    placeholder="Episode 1: Beginning"
                    className="bg-surface border-border"
                    data-testid="episode-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="epNumber">Episode Number</Label>
                  <Input
                    id="epNumber"
                    type="number"
                    min="1"
                    value={episodeData.episode_number}
                    onChange={(e) => setEpisodeData({ ...episodeData, episode_number: parseInt(e.target.value) })}
                    className="bg-surface border-border"
                    data-testid="episode-number"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Panels ({episodeData.panels.length})</Label>
                <label className="block border border-dashed border-border p-8 text-center cursor-pointer hover:border-primary transition-colors">
                  <Upload size={32} className="mx-auto text-text-secondary mb-2" />
                  <span className="text-text-secondary">Upload Panels</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePanelUpload} data-testid="panel-upload" />
                </label>
                
                {episodeData.panels.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {episodeData.panels.map((url, i) => (
                      <div key={`panel-${i}-${url.slice(-20)}`} className="relative group">
                        <img src={url} alt={`Panel ${i + 1}`} className="w-full aspect-[3/4] object-cover border border-border" />
                        <button
                          type="button"
                          onClick={() => removePanel(i)}
                          className="absolute top-1 right-1 p-1 bg-error text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={16} />
                        </button>
                        <span className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 text-xs">{i + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading} className="btn-neo w-full disabled:opacity-50" data-testid="create-episode-btn">
                {loading ? 'Creating...' : 'Create Episode'}
              </button>
            </form>
          </TabsContent>

          {/* Export PDF */}
          <TabsContent value="pdf">
            <div className="space-y-6">
              <p className="text-text-secondary">
                Upload your comic pages and export them as a printable PDF file.
              </p>

              <label className="block border border-dashed border-border p-8 text-center cursor-pointer hover:border-primary transition-colors">
                <Upload size={32} className="mx-auto text-text-secondary mb-2" />
                <span className="text-text-secondary">Add Pages</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePdfPageUpload} data-testid="pdf-upload" />
              </label>

              {pdfPages.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {pdfPages.map((url, i) => (
                    <div key={`pdf-page-${i}-${url.slice(-20)}`} className="relative group">
                      <img src={url} alt={`Page ${i + 1}`} className="w-full aspect-[3/4] object-cover border border-border" />
                      <button
                        type="button"
                        onClick={() => removePdfPage(i)}
                        className="absolute top-1 right-1 p-1 bg-error text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={16} />
                      </button>
                      <span className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 text-xs">{i + 1}</span>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={generatePdf}
                disabled={loading || pdfPages.length === 0}
                className="btn-neo w-full disabled:opacity-50"
                data-testid="generate-pdf-btn"
              >
                {loading ? 'Generating...' : `Generate PDF (${pdfPages.length} pages)`}
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CreatePage;
